import { useState, useEffect, useCallback, useMemo } from 'react';
import { getProductLayer, getProductRef, getProductView } from '../utils/tag-helpers';
import { Ugla3D, useUgla3dStateManager, useUgla3dModel } from '@ugla-france/ugla-3d-library';
import type { Asset } from '../types/ugla-types';
import type { LayerConfiguration, GroupedAssets, VapState } from '../types/vap-types';
import {
  getAvailableProductLayersByView,
  groupAssetsByViewAndLayerAndRef,
  getAvailableProductViews,
  getAssetsByViewAndLayerAndRef,
  getFirstAssetByViewAndLayerAndRef,
  getAvailableProductRefsForViewAndLayer,
  getFirstAssetByView,
  getAvailable360View,
} from '../utils/asset-helpers';
import {
  getVisibleAssets,
  calculateZOrdering,
  sortAssetsByZOrder,
  getDefaultLayerOrder,
  getBackgroundAssetsForView,
} from '../utils/visibility-helpers';
import {
  loadAndParseUglaFileFromUrl,
  loadAndParseUglaFileFromFile,
  UglaLoadError,
} from '../services/ugla-loader';

/**
 * Hook for managing VAP application state
 * Handles UGLA file loading, layer configuration, and asset management
 */
const useVapState = () : VapState => {
  const stateManager = useUgla3dStateManager();
  const model = useUgla3dModel();

  // State for layer configuration (which product_ref is selected for each product_layer)
  const [layerConfiguration, setLayerConfiguration] = useState<LayerConfiguration>({});
  const [currentView, setCurrentView] = useState<string>("0");
  const [currentModel, setCurrentModel] = useState<string>("");
  const [current360, setCurrent360] = useState<string>("");

  // State for loading status
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get all assets from the model
  const assets = useMemo(() => {
    try {
      return model.getContent().assets;
    } catch {
      return [];
    }
  }, [model]);

  useEffect(() => {
    setCurrentView(getAvailableProductViews(assets)[0]);
  }, [assets])

  useEffect(() => {
    setCurrent360(getAvailable360View(assets));
  }, [assets])

  // Group assets by product_layer and product_ref
  const groupedAssets = useMemo<GroupedAssets>(() => {
    return groupAssetsByViewAndLayerAndRef(assets);
  }, [assets]);

  // Get available product layers (categories)
  const availableLayers = useMemo(() => {
    return getAvailableProductLayersByView(assets, currentView);
  }, [assets, currentView]);

  const resetModel = useCallback(() => {
    stateManager.setModel(new Ugla3D(), {soft: false, resetSession: true});
  }, [stateManager])

  /**
   * Load a UGLA file from a URL
   */
  const loadFromUrl = useCallback(
    async (url: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const uglaFile = await loadAndParseUglaFileFromUrl(url);
        const newModel = new Ugla3D(JSON.stringify(uglaFile));
        stateManager.reset();
        stateManager.setModel(newModel);

        // Initialize layer configuration with first available ref for each layer
        const initialConfig: LayerConfiguration = {};
        const views = getAvailableProductViews(uglaFile.assets);
        views.forEach((view) => {
          const layers = getAvailableProductLayersByView(uglaFile.assets, view);
          initialConfig[view] = {};
          layers.forEach((layer) => {
            const refs = getAvailableProductRefsForViewAndLayer(uglaFile.assets, view, layer);
            if (refs.length > 0) {
              initialConfig[view][layer] = refs[0];
            }
          });
        });

        // Load all assets for all views
        Object.keys(initialConfig).forEach((view: string) => {
          // Get background and visible assets based on current configuration
          const visibleAssets = [...getBackgroundAssetsForView(newModel.getContent().assets, view), ...getVisibleAssets(newModel.getContent().assets, initialConfig, view)];

          // Sort assets by z-order for proper layer stacking
          const sortedAssets = sortAssetsByZOrder(visibleAssets, zOrderMap);

          // Add assets in z-order (lowest z-index first, so they render behind higher layers)
          sortedAssets.forEach((asset, index) => {
            // Use a unique ID based on view and layer and index
            const productView = getProductView(asset);
            const productLayer = getProductLayer(asset);
            const productRef = getProductRef(asset);
            const objectId = `${productView}-${productLayer || "layer"}-${productRef || ""}-${index}`

            stateManager.addObject({
              id: objectId,
              asset,
            });
          });
        });

        setLayerConfiguration(initialConfig);
      } catch (err) {
        const errorMessage =
          err instanceof UglaLoadError
            ? err.message
            : 'Failed to load UGLA file';
        setError(errorMessage);
        throw err;
      } finally {
        setInterval(() => setIsLoading(false), 1000);
      }
    },
    [stateManager]
  );

  /**
   * Load a UGLA file from a local File object
   */
  const loadFromFile = useCallback(
    async (file: File) => {
      setIsLoading(true);
      setError(null);

      try {
        const uglaFile = await loadAndParseUglaFileFromFile(file);
        stateManager.reset();
        stateManager.setModel(new Ugla3D(JSON.stringify(uglaFile)));

        // Initialize layer configuration with first available ref for each layer
        const initialConfig: LayerConfiguration = {};
        const views = getAvailableProductViews(uglaFile.assets);
        views.forEach((view) => {
          const layers = getAvailableProductLayersByView(uglaFile.assets, view);
          initialConfig[view] = {};
          layers.forEach((layer) => {
            const refs = getAvailableProductRefsForViewAndLayer(uglaFile.assets, view, layer);
            if (refs.length > 0) {
              initialConfig[view][layer] = refs[0];
            }
          });
        });
        setLayerConfiguration(initialConfig);
      } catch (err) {
        const errorMessage =
          err instanceof UglaLoadError
            ? err.message
            : 'Failed to load UGLA file';
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [stateManager]
  );

  /**
   * Switch the product reference for a specific product layer
   */
  const setProductRefForLayer = useCallback(
    (productLayer: string, productRef: string) => {
      setLayerConfiguration((prev) => {
        const newConfig = { ...prev };
        const views = getAvailableProductViews(assets);
        views.forEach((view) => {
          newConfig[view] = {
            ...newConfig[view],
            [productLayer]: productRef,
          };
        });
        return newConfig;
      });
    },
    [assets]
  );

  /**
   * Get available product references for a specific layer
   */
  const getProductRefsForLayer = useCallback(
    (productLayer: string): string[] => {
      return getAvailableProductRefsForViewAndLayer(assets, currentView, productLayer);
    },
    [assets, currentView]
  );

  /**
   * Get the currently selected product reference for a layer
   */
  const getSelectedRefForLayer = useCallback(
    (productLayer: string): string | undefined => {
      return layerConfiguration[currentView][productLayer];
    },
    [layerConfiguration, currentView]
  );

  /**
   * Get all assets that should be displayed based on current layer configuration
   */
  const getDisplayedAssets = useCallback((): Asset[] => {
    const displayedAssets: Asset[] = [];

    Object.keys(layerConfiguration).forEach((view) => {
      Object.entries(layerConfiguration[view]).forEach(([layer, ref]) => {
        const layerAssets = getAssetsByViewAndLayerAndRef(assets, view, layer, ref);
        displayedAssets.push(...layerAssets);
      });
    });

    return displayedAssets;
  }, [assets, layerConfiguration]);

  /**
   * Get a single representative asset for a layer/ref combination
   */
  const getAssetForLayerAndRef = useCallback(
    (productLayer: string, productRef: string): Asset | undefined => {
      return getFirstAssetByViewAndLayerAndRef(assets, currentView, productLayer, productRef);
    },
    [assets, currentView]
  );

  /**
   * Get a single representative asset for a view/layer/ref combination
   */
  const getAssetForViewAndLayerAndRef = useCallback(
    (productView: string, productLayer: string, productRef: string): Asset | undefined => {
      return getFirstAssetByViewAndLayerAndRef(assets, productView, productLayer, productRef);
    },
    [assets]
  );

  /**
   * Get a single representative asset for a view
   */
  const getAssetForView = useCallback(
    (productView: string): Asset | undefined => {
      return getFirstAssetByView(assets, productView);
    },
    [assets]
  );

  /**
   * Reset the layer configuration to initial state (first ref for each layer)
   */
  const resetLayerConfiguration = useCallback(() => {
    const initialConfig: LayerConfiguration = {};
    const views = getAvailableProductViews(assets);
    views.forEach((view) => {
      initialConfig[view] = {};
      const layers = getAvailableProductLayersByView(assets, view);
      layers.forEach((layer) => {
        const refs = getAvailableProductRefsForViewAndLayer(assets, view, layer);
        if (refs.length > 0) {
          initialConfig[view][layer] = refs[0];
        }
      });
    });
    setLayerConfiguration(initialConfig);
  }, [assets, availableLayers]);

  // Calculate z-ordering for proper layer stacking
  const zOrderMap = useMemo(() => {
    if (assets.length === 0) {
      return new Map<string, number>();
    }
    const layerOrder = getDefaultLayerOrder();
    return calculateZOrdering(assets, layerOrder);
  }, [assets]);

  // Update viewer when layer configuration changes
  useEffect(() => {

    if (isLoading){
      return;
    }

    if (assets.length === 0) {
      return;
    }

    if (!currentView || !layerConfiguration[currentView]){
      return;
    }

    // Get background and visible assets based on current configuration
    const visibleAssets = [...getBackgroundAssetsForView(assets, currentView), ...getVisibleAssets(assets, layerConfiguration, currentView)];

    if (visibleAssets.length === 0) {
      // No assets to display, clear viewer
      stateManager.reset();
      return;
    }

    // Sort assets by z-order for proper layer stacking
    const sortedAssets = sortAssetsByZOrder(visibleAssets, zOrderMap);

    // Clear existing objects
    // stateManager.reset();

    // Add assets in z-order (lowest z-index first, so they render behind higher layers)
    sortedAssets.forEach((asset, index) => {
      // Use a unique ID based on view and layer and index
      const productView = getProductView(asset);
      const productLayer = getProductLayer(asset);
      const productRef = getProductRef(asset);
      const objectId = `${productView}-${productLayer}` || `${productView}-layer-${productRef || ""}-${index}`
      
      stateManager.addObject({
        id: objectId,
        asset,
      })
      stateManager.removeObject(`(?!${objectId})\\d+-${productLayer}`, {regex: true});
    });
  }, [assets, layerConfiguration, stateManager, zOrderMap, currentView, isLoading]);

  return {
    // State
    isLoading,
    error,
    assets,
    groupedAssets,
    availableLayers,
    layerConfiguration,
    currentView,
    currentModel,
    current360,

    // Actions
    resetModel,
    loadFromUrl,
    loadFromFile,
    setCurrentView,
    setCurrentModel,
    setProductRefForLayer,
    getProductRefsForLayer,
    getSelectedRefForLayer,
    getDisplayedAssets,
    getAssetForView,
    getAssetForLayerAndRef,
    getAssetForViewAndLayerAndRef,
    resetLayerConfiguration,

    // UGLA library hooks
    // stateManager,
    // model,
  };
};

export default useVapState;
